/**
 * Still-image export: SVG, PNG and PDF of a scene.
 *
 * Every format starts from the SVG that `SvgRenderer` draws on screen, rendered
 * again for the whole drawing rather than the visible window (invariant 7,
 * R13.2). The formats differ only in what turns that SVG into a file:
 *
 * - SVG is the markup itself.
 * - PNG is the browser rasterising it, as it does for the screen, onto a
 *   transparent canvas at several pixels per screen pixel.
 * - PDF is the browser printing it on A3 (A4 for a script's steps), fitted to the page: vector, with the
 *   same fonts, text halos and dash patterns the screen shows. A PDF library would re-interpret the SVG and
 *   draw it differently, which is the divergence D13 rules out. It has a page
 *   per view (`PDF_VIEWS`): the full model, the SUS and instrument sides
 *   highlighted, and the notes, each with a legend naming it, all framed alike.
 *   The bills of materials chosen share a page after them.
 *
 * An SVG can carry the bills of materials too, as tables under the drawing.
 *
 * Editor furniture is not part of a still: no grid, selection, hit areas or
 * findings badges. A caption names the design and says whether the drawing is
 * a published version or a draft, so a picture in a slide deck is traceable to
 * what it shows (D35).
 */
import type { Bounds, Scene, SceneAnnotations } from "../scene";
import { SvgRenderer } from "./SvgRenderer";
import { THEME } from "./theme";

export type StillFormat = "svg" | "png" | "pdf";

const SVG_NS = "http://www.w3.org/2000/svg";
/** Space around the drawing, in scene units (screen pixels at 100%). */
const MARGIN = 16;
/** The caption band above the drawing. */
const CAPTION_HEIGHT = 28;
const CAPTION_TITLE_SIZE = 14;
const CAPTION_STATUS_SIZE = 12;
const CAPTION_GAP = 10;
/** The legend's swatch, the space after it, and the least space between caption and legend. */
const LEGEND_SWATCH = 12;
const LEGEND_SWATCH_GAP = 6;
const LEGEND_CLEARANCE = 32;
/** Between one entry of a key and the next. */
const LEGEND_ITEM_GAP = 16;
/** Lines under the drawing: the space above them, and each line's height. */
const FOOTER_GAP = 8;
const FOOTER_LINE = 18;
/** An average character's width, as a share of its font size: to wrap footer lines. */
const CHAR_WIDTH = 0.55;
/**
 * A table's lines: its heading, the note under it, and each row. Columns are
 * sized from a generous character width, so a cell never runs into the next.
 */
const TABLE_TITLE = 22;
const TABLE_NOTE = 18;
const TABLE_ROW = 20;
const TABLE_CELL_GAP = 20;
const TABLE_CHAR_WIDTH = 0.6;
/** Between tables side by side, and between the drawing and the tables under it. */
const TABLE_GAP = 40;
const TABLE_TOP_GAP = 16;
/**
 * Without a layout to measure in (tests), how far labels and badges may reach
 * beyond the geometric bounds, which cover symbols and lines only.
 */
const FALLBACK_OVERHANG = 48;
/** PNG pixels per screen pixel at 100% zoom: print quality on a slide. */
export const PNG_SCALE = 4;
/** Canvas sizes every current browser will allocate. */
const MAX_CANVAS_SIDE = 16384;
const MAX_CANVAS_AREA = 16384 * 8192;

export interface StillCaption {
  title: string;
  status: string;
  /** What the sheet shows, or a key to its colours, as a legend at the right of the caption band. */
  legend?: StillLegend | readonly StillLegend[];
  /** Paragraphs under the drawing, wrapped to the sheet's width. */
  footer?: readonly string[];
}

/** A view's name, keyed by a swatch of how it looks on the drawing. */
export interface StillLegend {
  label: string;
  swatch: { fill: string; stroke?: string; opacity?: number } | null;
}

/** A table on a sheet: a bill of materials, headed by a legend entry. */
export interface StillTable {
  legend: StillLegend;
  note?: string;
  columns: readonly [string, string, string];
  rows: readonly { item: string; detail?: string | null; quantity: string; unit: string }[];
  /** Said in place of the rows when there are none. */
  empty: string;
}

export interface Still {
  /** A standalone SVG document. */
  svg: string;
  /** In screen pixels at 100% zoom. */
  width: number;
  height: number;
}

/** What the drawing is: a published version as is, or a draft. */
export interface StillSource {
  name: string;
  /** The published version the drawing is identical to, or null for a draft. */
  version: number | null;
  /** The draft's saved revision. */
  revision: number | null;
  /** Edits on screen not yet saved into `revision`. */
  unsaved: boolean;
}

export function stillCaption(source: StillSource): StillCaption {
  if (source.version !== null) return { title: source.name, status: `v${source.version}` };
  const status =
    source.revision === null
      ? "Draft"
      : `Draft · revision ${source.revision}${source.unsaved ? " with unsaved edits" : ""}`;
  return { title: source.name, status };
}

export function stillFilename(source: StillSource, extension: string): string {
  const slug =
    source.name
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "design";
  const which =
    source.version !== null ? `v${source.version}` : source.revision === null ? "draft" : `draft-r${source.revision}`;
  return `${slug}-${which}.${extension}`;
}

// --- Composition ------------------------------------------------------------

/** The part of the scene a still shows, margin included, in scene units. */
interface Frame {
  x: number;
  y: number;
  width: number;
  height: number;
}

function frameAround(bounds: Bounds, overhang: number): Frame {
  const reach = overhang + MARGIN;
  // Whole units, so the drawing's origin and a PNG's pixel grid agree.
  const x = Math.floor(bounds.minX - reach);
  const y = Math.floor(bounds.minY - reach);
  return {
    x,
    y,
    width: Math.ceil(bounds.maxX + reach) - x,
    height: Math.ceil(bounds.maxY + reach) - y,
  };
}

function Sheet({
  scene,
  caption,
  frame,
  width,
  height,
  legendWidths,
  idPrefix,
  footer = [],
  tables = [],
  tablesTop = 0,
}: {
  scene: Scene | null;
  caption: StillCaption;
  frame: Frame;
  width: number;
  height: number;
  tables?: readonly StillTable[];
  /** Where the tables start, under the drawing and its footer. */
  tablesTop?: number;
  /** The footer, wrapped into lines. */
  footer?: readonly string[];
  /** Each legend entry's own width, so the legend can end at the right margin. */
  legendWidths: readonly number[];
  /** Unique per sheet: pages of one PDF share a document, and so its ids. */
  idPrefix: string;
}) {
  const footerTop = CAPTION_HEIGHT + frame.height + FOOTER_GAP;
  return (
    <svg
      xmlns={SVG_NS}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fontFamily={THEME.fontFamily}
    >
      <text data-still="caption" x={MARGIN} y={MARGIN + CAPTION_TITLE_SIZE / 2} dominantBaseline="central">
        <tspan fontSize={CAPTION_TITLE_SIZE} fontWeight={600} fill={THEME.ink}>
          {caption.title}
        </tspan>
        <tspan dx={CAPTION_GAP} fontSize={CAPTION_STATUS_SIZE} fill={THEME.muted}>
          {caption.status}
        </tspan>
      </text>
      <Legend items={legendItems(caption)} widths={legendWidths} right={width - MARGIN} />
      {scene && (
        <g transform={`translate(0 ${CAPTION_HEIGHT})`}>
          {/* The screen's renderer, with the viewport over the whole drawing at 100%. */}
          <SvgRenderer
            scene={scene}
            width={frame.width}
            height={frame.height}
            viewport={{ x: frame.x, y: frame.y, zoom: 1 }}
            title={`${caption.title} schematic`}
            idPrefix={idPrefix}
          />
        </g>
      )}
      {footer.length > 0 && (
        <text data-still="footer" fontSize={CAPTION_STATUS_SIZE} fill={THEME.ink}>
          {footer.map((line, index) => (
            <tspan key={index} x={MARGIN} y={footerTop + (index + 0.5) * FOOTER_LINE} dominantBaseline="central">
              {line}
            </tspan>
          ))}
        </text>
      )}
      {tables.length > 0 && <Tables tables={tables} top={tablesTop} />}
    </svg>
  );
}

function tableTextWidth(text: string): number {
  return text.length * CAPTION_STATUS_SIZE * TABLE_CHAR_WIDTH;
}

function rowItem(row: StillTable["rows"][number]): string {
  return row.detail ? `${row.item} · ${row.detail}` : row.item;
}

/** A table's column widths and its size. */
function tableLayout(table: StillTable): { columns: [number, number, number]; width: number; height: number } {
  const column = (heading: string, cells: readonly string[]) => Math.max(tableTextWidth(heading), ...cells.map(tableTextWidth));
  const columns: [number, number, number] = [
    column(table.columns[0], table.rows.map(rowItem)),
    column(table.columns[1], table.rows.map((row) => row.quantity)),
    column(table.columns[2], table.rows.map((row) => row.unit)),
  ];
  const width = Math.max(
    columns[0] + columns[1] + columns[2] + 2 * TABLE_CELL_GAP,
    LEGEND_SWATCH + LEGEND_SWATCH_GAP + tableTextWidth(table.legend.label),
    tableTextWidth(table.note ?? ""),
    tableTextWidth(table.empty),
  );
  const height = TABLE_TITLE + (table.note ? TABLE_NOTE : 0) + TABLE_ROW * (1 + Math.max(1, table.rows.length));
  return { columns, width: Math.ceil(width), height };
}

/** Tables side by side: the space they take together. */
function tablesSize(tables: readonly StillTable[]): { width: number; height: number } {
  const layouts = tables.map(tableLayout);
  return {
    width: layouts.reduce((sum, layout) => sum + layout.width, 0) + TABLE_GAP * Math.max(0, layouts.length - 1),
    height: Math.max(0, ...layouts.map((layout) => layout.height)),
  };
}

/** Each table: its heading keyed as a legend is, its note, and a row per line, quantities right-aligned. */
function Tables({ tables, top }: { tables: readonly StillTable[]; top: number }) {
  let left = MARGIN;
  return (
    <g data-still="tables" fontSize={CAPTION_STATUS_SIZE}>
      {tables.map((table, index) => {
        const { columns, width } = tableLayout(table);
        const at = left;
        left += width + TABLE_GAP;
        const swatch = table.legend.swatch;
        const textLeft = swatch ? LEGEND_SWATCH + LEGEND_SWATCH_GAP : 0;
        const head = TABLE_TITLE + (table.note ? TABLE_NOTE : 0);
        const quantityRight = columns[0] + TABLE_CELL_GAP + columns[1];
        const unitLeft = quantityRight + TABLE_CELL_GAP;
        const middle = (row: number) => head + (row + 0.5) * TABLE_ROW;
        return (
          <g key={index} data-still="table" transform={`translate(${at} ${top})`}>
            {swatch && (
              <rect
                x={0}
                y={TABLE_TITLE / 2 - LEGEND_SWATCH / 2}
                width={LEGEND_SWATCH}
                height={LEGEND_SWATCH}
                rx={2}
                fill={swatch.fill}
                fillOpacity={swatch.opacity}
                stroke={swatch.stroke}
                strokeWidth={swatch.stroke ? 1 : undefined}
              />
            )}
            <text data-still="table-title" x={textLeft} y={TABLE_TITLE / 2} dominantBaseline="central" fontWeight={600} fill={THEME.ink}>
              {table.legend.label}
            </text>
            {table.note && (
              <text x={0} y={TABLE_TITLE + TABLE_NOTE / 2} dominantBaseline="central" fill={THEME.muted}>
                {table.note}
              </text>
            )}
            <g data-still="table-head" fontWeight={600} fill={THEME.muted}>
              <text x={0} y={middle(0)} dominantBaseline="central">
                {table.columns[0]}
              </text>
              <text x={quantityRight} y={middle(0)} dominantBaseline="central" textAnchor="end">
                {table.columns[1]}
              </text>
              <text x={unitLeft} y={middle(0)} dominantBaseline="central">
                {table.columns[2]}
              </text>
            </g>
            <line x1={0} x2={width} y1={head + TABLE_ROW} y2={head + TABLE_ROW} stroke={THEME.line} strokeWidth={1} />
            {table.rows.length === 0 ? (
              <text x={0} y={middle(1)} dominantBaseline="central" fill={THEME.muted}>
                {table.empty}
              </text>
            ) : (
              table.rows.map((row, line) => (
                <g key={line} data-still="table-row">
                  <text x={0} y={middle(line + 1)} dominantBaseline="central" fill={THEME.ink}>
                    {row.item}
                    {row.detail && (
                      // Its own baseline: some renderers do not pass the text's down to a tspan.
                      <tspan dominantBaseline="central" fill={THEME.muted}>{` · ${row.detail}`}</tspan>
                    )}
                  </text>
                  <text x={quantityRight} y={middle(line + 1)} dominantBaseline="central" textAnchor="end" fill={THEME.ink}>
                    {row.quantity}
                  </text>
                  <text x={unitLeft} y={middle(line + 1)} dominantBaseline="central" fill={THEME.muted}>
                    {row.unit}
                  </text>
                  {line < table.rows.length - 1 && (
                    <line
                      x1={0}
                      x2={width}
                      y1={head + (line + 2) * TABLE_ROW}
                      y2={head + (line + 2) * TABLE_ROW}
                      stroke={THEME.line}
                      strokeOpacity={0.4}
                      strokeWidth={0.75}
                    />
                  )}
                </g>
              ))
            )}
          </g>
        );
      })}
    </g>
  );
}

function legendItems(caption: StillCaption): readonly StillLegend[] {
  const legend = caption.legend;
  return legend === undefined ? [] : Array.isArray(legend) ? legend : [legend as StillLegend];
}

/** A legend's width: its entries, and the gaps between them. */
function legendSpan(widths: readonly number[]): number {
  return widths.length ? widths.reduce((sum, width) => sum + width, 0) + LEGEND_ITEM_GAP * (widths.length - 1) : 0;
}

/** The legend, right-aligned in the caption band: each entry a swatch, then its name. */
function Legend({ items, widths, right }: { items: readonly StillLegend[]; widths: readonly number[]; right: number }) {
  if (items.length === 0) return null;
  const y = MARGIN + CAPTION_TITLE_SIZE / 2;
  let left = right - legendSpan(widths);
  return (
    <g data-still="legend">
      {items.map((legend, index) => {
        const at = left;
        left += (widths[index] ?? 0) + LEGEND_ITEM_GAP;
        const textLeft = legend.swatch ? LEGEND_SWATCH + LEGEND_SWATCH_GAP : 0;
        return (
          <g key={index} data-still="legend-item" transform={`translate(${at} 0)`}>
            {legend.swatch && (
              <rect
                x={0}
                y={y - LEGEND_SWATCH / 2}
                width={LEGEND_SWATCH}
                height={LEGEND_SWATCH}
                rx={2}
                fill={legend.swatch.fill}
                fillOpacity={legend.swatch.opacity}
                stroke={legend.swatch.stroke}
                strokeWidth={legend.swatch.stroke ? 1 : undefined}
              />
            )}
            <text
              x={textLeft}
              y={y}
              dominantBaseline="central"
              fontSize={CAPTION_STATUS_SIZE}
              fontWeight={600}
              fill={THEME.ink}
            >
              {legend.label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

/** Serialise, leaving out what is only there for the pointer. */
function standalone(markup: string): string {
  const parsed = new DOMParser().parseFromString(markup, "image/svg+xml");
  // Invisible hit areas: they draw nothing, but an SVG editor would show them.
  parsed.querySelectorAll("[data-hit]").forEach((element) => element.remove());
  return new XMLSerializer().serializeToString(parsed.documentElement);
}

interface Measured {
  /** Null on a sheet with no drawing. */
  drawing: Bounds | null;
  captionWidth: number;
  legendWidths: number[];
}

/**
 * The drawing's true extent, labels and badges included, from the browser's
 * layout. The scene's bounds cover symbols and lines only; text is measured
 * rather than estimated, so a still is never cropped; the caption and legend
 * are measured on every sheet, drawn or not. Null without layout.
 */
function measure(markup: string): Measured | null {
  if (typeof document === "undefined") return null;
  const host = document.createElement("div");
  // Laid out but not seen: getBBox needs layout, which display: none prevents.
  host.style.cssText = "position:fixed;left:-100000px;top:0;visibility:hidden;pointer-events:none";
  host.innerHTML = markup;
  document.body.append(host);
  try {
    const layer = host.querySelector<SVGGraphicsElement>('[data-layer="scene"]');
    const caption = host.querySelector<SVGGraphicsElement>('[data-still="caption"]');
    if (!caption || typeof caption.getBBox !== "function") return null;
    const box = layer?.getBBox();
    if (box && !(box.width > 0 && box.height > 0)) return null;
    const items = [...host.querySelectorAll<SVGGraphicsElement>('[data-still="legend-item"]')];
    return {
      drawing: box ? { minX: box.x, minY: box.y, maxX: box.x + box.width, maxY: box.y + box.height } : null,
      captionWidth: caption.getBBox().width,
      legendWidths: items.map((item) => item.getBBox().width),
    };
  } finally {
    host.remove();
  }
}

/** Rough caption width, for when nothing can be measured. */
function estimateCaption(caption: StillCaption): number {
  return (caption.title.length * CAPTION_TITLE_SIZE + caption.status.length * CAPTION_STATUS_SIZE) * 0.62 + CAPTION_GAP;
}

/** Rough legend entry widths, likewise. */
function estimateLegend(caption: StillCaption): number[] {
  return legendItems(caption).map(
    (legend) => (legend.swatch ? LEGEND_SWATCH + LEGEND_SWATCH_GAP : 0) + legend.label.length * CAPTION_STATUS_SIZE * 0.62,
  );
}

/** The sheet width the caption band needs: caption, then the legend clear of it. */
function bandWidth(captionWidth: number, legendWidths: readonly number[]): number {
  const legendWidth = legendSpan(legendWidths);
  return captionWidth + (legendWidth > 0 ? LEGEND_CLEARANCE + legendWidth : 0) + 2 * MARGIN;
}

function union(boxes: readonly Bounds[]): Bounds {
  return {
    minX: Math.min(...boxes.map((box) => box.minX)),
    minY: Math.min(...boxes.map((box) => box.minY)),
    maxX: Math.max(...boxes.map((box) => box.maxX)),
    maxY: Math.max(...boxes.map((box) => box.maxY)),
  };
}

/** Paragraphs broken into lines that fit `width`, at word boundaries where there are any. */
export function wrapLines(paragraphs: readonly string[], width: number): string[] {
  const perLine = Math.max(20, Math.floor((width - 2 * MARGIN) / (CAPTION_STATUS_SIZE * CHAR_WIDTH)));
  return paragraphs.flatMap((paragraph) =>
    paragraph.split("\n").flatMap((text) => {
      const lines: string[] = [];
      let line = "";
      for (const word of text.split(/\s+/).filter(Boolean)) {
        for (let rest = word; rest; ) {
          const candidate = line ? `${line} ${rest}` : rest;
          if (candidate.length <= perLine) {
            line = candidate;
            rest = "";
          } else if (line) {
            lines.push(line);
            line = "";
          } else {
            // A word longer than a line: broken where it must be.
            lines.push(rest.slice(0, perLine));
            rest = rest.slice(perLine);
          }
        }
      }
      return line ? [...lines, line] : lines;
    }),
  );
}

/**
 * One sheet of a still: a scene, and the caption saying what it shows; tables
 * under it, or on their own where there is no scene.
 */
export interface StillSheet {
  scene: Scene | null;
  caption: StillCaption;
  tables?: readonly StillTable[];
}

/**
 * Several scenes of one design as standalone SVGs, sharing one frame: the
 * same size, with the drawing in the same place on each, so pages of a PDF
 * can be compared by flicking between them. The frame covers everything any
 * sheet draws (a note on one page is not cropped, nor the drawing shifted
 * on the others). A sheet with tables is as wide as the rest, and taller only
 * where its tables need it.
 */
export async function composeStills(sheets: readonly StillSheet[]): Promise<Still[]> {
  // Loaded on first export: the static renderer is not needed to edit.
  const { renderToStaticMarkup } = await import("react-dom/server");
  const render = (
    index: number,
    frame: Frame,
    width: number,
    height: number,
    legendWidths: readonly number[],
    footer: readonly string[] = [],
    tablesTop = 0,
  ) => {
    const { scene, caption, tables } = sheets[index]!;
    const idPrefix = sheets.length === 1 ? "still" : `still-${index + 1}`;
    return renderToStaticMarkup(
      <Sheet
        scene={scene}
        caption={caption}
        frame={frame}
        width={width}
        height={height}
        legendWidths={legendWidths}
        idPrefix={idPrefix}
        footer={footer}
        tables={tables}
        tablesTop={tablesTop}
      />,
    );
  };

  const scenes = sheets.flatMap((sheet) => (sheet.scene ? [sheet.scene] : []));
  let frame = scenes.length > 0 ? frameAround(union(scenes.map((scene) => scene.bounds)), FALLBACK_OVERHANG) : NO_FRAME;
  let bands = sheets.map((sheet) => ({
    caption: estimateCaption(sheet.caption),
    legend: estimateLegend(sheet.caption),
  }));
  // Hit areas out first: their wide invisible strokes are not the drawing.
  const trial = Math.max(frame.width, ...bands.map((band) => bandWidth(band.caption, band.legend)));
  const measured = sheets.map((_, index) =>
    measure(standalone(render(index, frame, trial, CAPTION_HEIGHT + frame.height, bands[index]!.legend))),
  );
  if (measured.every((each) => each !== null)) {
    if (scenes.length > 0) frame = frameAround(union(measured.flatMap((each) => (each!.drawing ? [each!.drawing] : []))), 0);
    bands = measured.map((each) => ({ caption: each!.captionWidth, legend: each!.legendWidths }));
  }
  const tables = sheets.map((sheet) => tablesSize(sheet.tables ?? []));
  const width = Math.ceil(
    Math.max(
      frame.width,
      ...bands.map((band) => bandWidth(band.caption, band.legend)),
      ...tables.map((size) => (size.width > 0 ? size.width + 2 * MARGIN : 0)),
    ),
  );
  const footers = sheets.map((sheet) => wrapLines(sheet.caption.footer ?? [], width));
  const lines = Math.max(0, ...footers.map((footer) => footer.length));
  const footerHeight = lines > 0 ? FOOTER_GAP + lines * FOOTER_LINE + MARGIN / 2 : 0;
  const height = CAPTION_HEIGHT + frame.height + footerHeight;
  return sheets.map((sheet, index) => {
    const tablesTop = CAPTION_HEIGHT + (sheet.scene ? frame.height + footerHeight : 0) + TABLE_TOP_GAP;
    const own = tables[index]!.height > 0 ? Math.ceil(Math.max(height, tablesTop + tables[index]!.height + MARGIN)) : height;
    return {
      svg: standalone(render(index, frame, width, own, bands[index]!.legend, footers[index], tablesTop)),
      width,
      height: own,
    };
  });
}

/** The frame of a set of sheets none of which has a drawing. */
const NO_FRAME: Frame = { x: 0, y: 0, width: 0, height: 0 };

/** The scene as a standalone SVG, the whole drawing, captioned, with any tables under it. */
export async function composeStill(scene: Scene, caption: StillCaption, tables: readonly StillTable[] = []): Promise<Still> {
  const [still] = await composeStills([{ scene, caption, tables }]);
  return still!;
}

// --- Files --------------------------------------------------------------------

function svgFile(still: Still): Blob {
  return new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n', still.svg], { type: "image/svg+xml" });
}

/** The PNG scale actually used: PNG_SCALE unless the canvas would be too big. */
export function pngScale(still: Pick<Still, "width" | "height">, wanted = PNG_SCALE): number {
  const bySide = MAX_CANVAS_SIDE / Math.max(still.width, still.height);
  const byArea = Math.sqrt(MAX_CANVAS_AREA / (still.width * still.height));
  return Math.max(1, Math.min(wanted, bySide, byArea));
}

/** The same SVG at another size: the viewBox keeps the drawing, the size sets the pixels. */
function resized(still: Still, width: number, height: number): string {
  const parsed = new DOMParser().parseFromString(still.svg, "image/svg+xml");
  parsed.documentElement.setAttribute("width", String(width));
  parsed.documentElement.setAttribute("height", String(height));
  return new XMLSerializer().serializeToString(parsed.documentElement);
}

/**
 * Rasterise through the browser's own SVG renderer onto a canvas that starts
 * transparent and is never filled: only the drawing has pixels.
 */
export async function pngFile(still: Still, wanted = PNG_SCALE): Promise<Blob> {
  const scale = pngScale(still, wanted);
  const width = Math.round(still.width * scale);
  const height = Math.round(still.height * scale);
  const image = new Image();
  // A data URL, not a blob URL: some browsers taint a canvas drawn from a blob SVG.
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(resized(still, width, height))}`;
  await image.decode();
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser could not make a PNG.");
  context.drawImage(image, 0, 0, width, height);
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("This browser could not make a PNG."))),
      "image/png",
    ),
  );
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.append(link);
  link.click();
  link.remove();
  // After the click has handed the URL to the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
}

/** The PDF's paper, landscape, in millimetres: A3 for a drawing, A4 for a script's steps. */
export type Paper = "A3" | "A4";
export const PAPER: Readonly<Record<Paper, { width: number; height: number }>> = {
  A3: { width: 420, height: 297 },
  A4: { width: 297, height: 210 },
};
const PAGE_MARGIN_MM = 10;
const MM_PER_PX = 25.4 / 96;

/**
 * Where the drawing sits on the page: always landscape, and scaled to fill
 * the printable area. Never turned to suit a tall drawing: a browser that
 * ignores `@page size` prints on the paper chosen in its dialog, and a
 * portrait layout on landscape paper split each page in two. Sizes are
 * absolute millimetres, never viewport units: while printing, a viewport is
 * whatever the browser says it is, and a drawing sized to one was cut off at
 * the page edge.
 */
export function pageLayout(still: Pick<Still, "width" | "height">, paper: Paper) {
  const page = PAPER[paper];
  const area = { width: page.width - 2 * PAGE_MARGIN_MM, height: page.height - 2 * PAGE_MARGIN_MM };
  const natural = { width: still.width * MM_PER_PX, height: still.height * MM_PER_PX };
  const scale = Math.min(area.width / natural.width, area.height / natural.height);
  const round = (mm: number) => Math.floor(mm * 100) / 100;
  return {
    orientation: "landscape" as const,
    page,
    area,
    drawing: { width: round(natural.width * scale), height: round(natural.height * scale) },
  };
}

export function a3Layout(still: Pick<Still, "width" | "height">) {
  return pageLayout(still, "A3");
}

/**
 * A document with one page per still, each drawing fitted and centred,
 * for the browser to print. Drawings share a size (`composeStills`), so
 * their pages have the same scale; a taller page of tables is fitted on its own.
 */
export function printDocument(stills: Still | readonly Still[], title: string, paper: Paper = "A3"): string {
  const pages = Array.isArray(stills) ? stills : [stills as Still];
  const { orientation, area } = pageLayout(pages[0]!, paper);
  const sized = pages.map((still) => {
    const { drawing } = pageLayout(still, paper);
    const parsed = new DOMParser().parseFromString(still.svg, "image/svg+xml");
    // The viewBox keeps the drawing; the size in millimetres puts it on the page.
    parsed.documentElement.setAttribute("width", `${drawing.width}mm`);
    parsed.documentElement.setAttribute("height", `${drawing.height}mm`);
    return `<section class="page">${new XMLSerializer().serializeToString(parsed.documentElement)}</section>`;
  });
  // Each page is a hair shorter than the printable area, so rounding in the
  // browser's layout never spills a blank page after it.
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
@page { size: ${paper} ${orientation}; margin: ${PAGE_MARGIN_MM}mm; }
html, body { margin: 0; padding: 0; }
body {
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;
}
.page {
  width: ${area.width}mm;
  height: ${area.height - 1}mm;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  break-after: page;
}
.page:last-child { break-after: auto; }
svg { display: block; flex: none; }
</style></head><body>${sized.join("")}</body></html>`;
}

/**
 * Print to PDF: the browser's print dialog, with the page sized to the
 * drawing. The dialog's "Save as PDF" writes it; the suggested file name is
 * the document title.
 */
export function printPdf(stills: readonly Still[], filename: string, paper: Paper = "A3"): Promise<void> {
  return new Promise((resolve, reject) => {
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.tabIndex = -1;
    // Off screen but given a real size: some browsers lay a zero-sized frame
    // out at zero before printing it.
    // The landscape paper at 96 dpi, its shape.
    const px = (mm: number) => Math.round(mm / MM_PER_PX);
    frame.style.cssText = `position:fixed;left:-10000px;top:0;width:${px(PAPER[paper].width)}px;height:${px(PAPER[paper].height)}px;border:0`;
    const title = filename.replace(/\.pdf$/, "");
    const pageTitle = document.title;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      document.title = pageTitle;
      frame.remove();
      resolve();
    };
    frame.onload = () => {
      const view = frame.contentWindow;
      if (!view) {
        frame.remove();
        reject(new Error("This browser could not print the drawing."));
        return;
      }
      // Some browsers name the PDF after the top document, some after the frame.
      document.title = title;
      view.addEventListener("afterprint", finish);
      view.focus();
      view.print();
      // print() blocks in most browsers; where it does not, afterprint ends it,
      // and this catches a browser that sends neither.
      window.setTimeout(finish, 60_000);
    };
    frame.srcdoc = printDocument(stills, title, paper);
    document.body.append(frame);
  });
}

// --- The PDF's pages ------------------------------------------------------------

/**
 * One page of the PDF: how it overrides the designer's view, and its legend.
 * Labels, dimensions and which kinds of line are drawn stay as the designer
 * has them on screen; the wash and notes are set per page.
 */
export type PdfViewId = "full" | "sus" | "instrument" | "notes";

export interface PdfView {
  id: PdfViewId;
  annotations: Pick<SceneAnnotations, "wash" | "isolate" | "notes">;
  legend: StillLegend;
  /**
   * Why the page would show nothing the full model does not, or null when it
   * has something of its own. Such a page cannot be chosen.
   */
  unavailable: (scene: Scene) => string | null;
}

export const PDF_VIEWS: readonly PdfView[] = [
  {
    id: "full",
    annotations: { wash: "none", isolate: false, notes: false },
    legend: { label: "Full model", swatch: null },
    unavailable: () => null,
  },
  {
    id: "sus",
    // With no line across an external interface there is no side to wash.
    annotations: { wash: "inside", isolate: false, notes: false },
    legend: { label: "SUS view", swatch: { fill: THEME.washInside } },
    unavailable: (scene) => (scene.washed.size > 0 ? null : "No line crosses an external interface"),
  },
  {
    id: "instrument",
    annotations: { wash: "outside", isolate: false, notes: false },
    legend: { label: "Instrument view", swatch: { fill: THEME.washOutside } },
    unavailable: (scene) => (scene.washed.size > 0 ? null : "No line crosses an external interface"),
  },
  {
    id: "notes",
    annotations: { wash: "none", isolate: false, notes: true },
    legend: { label: "Notes", swatch: { fill: THEME.note, stroke: THEME.noteEdge } },
    unavailable: (scene) => (scene.notes.length > 0 ? null : "The design has no notes"),
  },
];

/** Draws the design with the screen's view, less the annotations given. */
export type DrawScene = (annotations: Partial<SceneAnnotations>) => Scene;

/** Each PDF page, and why it cannot be chosen (null when it can). */
export function pdfAvailability(draw: DrawScene): { view: PdfView; unavailable: string | null }[] {
  return PDF_VIEWS.map((view) => ({ view, unavailable: view.unavailable(draw(view.annotations)) }));
}

/**
 * The PDF's sheets: the chosen views that can be drawn, in `PDF_VIEWS` order,
 * then the tables given (the bills of materials) together on a page of their own.
 */
export function pdfSheets(
  draw: DrawScene,
  source: StillSource,
  chosen: ReadonlySet<PdfViewId>,
  tables: readonly StillTable[] = [],
): StillSheet[] {
  const caption = stillCaption(source);
  const views = PDF_VIEWS.flatMap((view) => {
    if (!chosen.has(view.id)) return [];
    const scene = draw(view.annotations);
    return view.unavailable(scene) === null ? [{ scene, caption: { ...caption, legend: view.legend } }] : [];
  });
  if (tables.length === 0) return views;
  const legend: StillLegend = { label: tables.length === 1 ? tables[0]!.legend.label : "Bills of materials", swatch: null };
  return [...views, { scene: null, caption: { ...caption, legend }, tables }];
}

/**
 * Export a design as a file, built by the screen's renderer. SVG and PNG are
 * the screen as it is; the PDF has a page per chosen view (`PDF_VIEWS`). The
 * tables (the bills of materials chosen) go under the SVG's drawing, and on a
 * page of the PDF's own; a PNG has none.
 */
export async function exportStill(
  draw: DrawScene,
  source: StillSource,
  format: StillFormat,
  pages: ReadonlySet<PdfViewId> = new Set(PDF_VIEWS.map((view) => view.id)),
  tables: readonly StillTable[] = [],
): Promise<void> {
  const filename = stillFilename(source, format);
  if (format === "pdf") {
    const sheets = pdfSheets(draw, source, pages, tables);
    if (sheets.length === 0) throw new Error("No page was chosen.");
    await printPdf(await composeStills(sheets), filename);
    return;
  }
  const still = await composeStill(draw({}), stillCaption(source), format === "svg" ? tables : []);
  if (format === "svg") downloadBlob(svgFile(still), filename);
  else downloadBlob(await pngFile(still), filename);
}
